import {animate, style, transition, trigger} from '@angular/animations';
import {CommonModule} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule} from '@angular/material/menu';
import {Store} from '@ngrx/store';
import {Observable, Subscription} from 'rxjs';
import {suffixOptions} from 'src/app/app.constants';
import {ChallengeBuilderProject, ChallengeMetric} from 'src/app/core/model/project';
import {
  ChallengeMetricsAction,
  SetBackLoadingAction,
  SetNextLoadingAction,
  UpdateChallengeNextValidAction,
} from 'src/app/core/store/challenge-builder/challenge-builder.action';
import {MessageService} from 'src/app/services/message.service';
import {OphXButtonComponent} from 'src/app/shared/design/oph-x-button/oph-x-button.component';
import {TasksService} from 'src/app/shared/services/tasks.service';
import {TrackerMetricTypes} from 'src/app/trackers/shared/tracker-metric-types';
import {CbTaskIconButtonComponent} from '../shared/cb-task-icon-button/cb-task-icon-button.component';

interface MetricTypeOption {
  value: TrackerMetricTypes;
  display: string;
}

interface MetricSuffixOption {
  value: string;
  display: string;
}

interface LocalMetric extends ChallengeMetric {
  uid: string;
}

@Component({
  selector: 'cb-metrics',
  standalone: true,
  imports: [CommonModule, MatMenuModule, OphXButtonComponent, MatIconModule, CbTaskIconButtonComponent],
  templateUrl: './cb-metrics.component.html',
  styleUrl: './cb-metrics.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('metricAnimation', [
      transition(':enter', [
        style({opacity: 0, height: 0, overflow: 'hidden', marginBottom: 0}),
        animate('200ms ease-out', style({opacity: 1, height: '*', marginBottom: '*'})),
      ]),
      transition(':leave', [
        style({overflow: 'hidden'}),
        animate('200ms ease-in', style({opacity: 0, height: 0, marginBottom: 0})),
      ]),
    ]),
  ],
})
export class CbMetricsComponent implements OnInit {
  private nextClickSubscription?: Subscription;
  private backClickSubscription?: Subscription;
  private readonly store$ = inject(Store);
  private readonly messageService = inject(MessageService);
  private readonly tasksService = inject(TasksService);
  private readonly destroyRef = inject(DestroyRef);
  private destroyed = false;

  nextClick$ = input.required<Observable<void>>();
  backClick$ = input.required<Observable<void>>();
  cbProject = input<ChallengeBuilderProject | undefined>();
  step = input<string>();

  onNext = output<void>();
  onBack = output<void>();

  // Signal for local metrics (flattened from all tasks)
  localMetrics = signal<LocalMetric[]>([]);

  // Track task icons by taskId
  taskIcons = signal<Record<string, string>>({});

  private uidCounter = 0;
  private generateUid(): string {
    return `metric-${++this.uidCounter}-${Date.now()}`;
  }

  // Store initial metrics for comparison
  private initialMetrics: ChallengeMetric[] = [];

  // Effect to initialize metrics when project data becomes available
  // @ts-expect-error - Effect must be assigned to trigger registration
  private readonly projectEffect = effect(
    () => {
      const cbProject = this.cbProject();
      if (cbProject?.challengeBuilder?.objectives) {
        // Assemble metrics from objectives -> tasks -> metrics to preserve target data
        const metrics: ChallengeMetric[] = (cbProject.challengeBuilder.objectives || []).flatMap(objective =>
          (objective.tasks || []).flatMap(task =>
            (task.metrics || []).map(metric => ({
              ...metric,
              taskId: metric.taskId || task._id,
            }))
          )
        );

        // Only initialize if we haven't yet or if metrics are empty
        if (this.initialMetrics.length === 0 || this.localMetrics().length === 0) {
          this.initialMetrics = structuredClone(metrics);
          const localMetrics: LocalMetric[] = metrics.map(m => ({
            ...m,
            uid: this.generateUid(),
          }));
          this.localMetrics.set(localMetrics);

          // Auto-populate task icons from iconSuggestion
          this.initTaskIcons(cbProject);
        }
      }
    },
    {allowSignalWrites: true}
  );

  // Ensure every selected task has at least one metric in localMetrics (empty placeholder if none)
  // @ts-expect-error - Effect must be assigned to trigger registration
  private readonly ensureEmptyMetricPerTaskEffect = effect(
    () => {
      const tasks = this.selectedTasks();
      const metrics = this.localMetrics();
      const taskIdsWithMetrics = new Set(metrics.map(m => m.taskId).filter(Boolean));
      const missingTaskIds = tasks
        .map(t => t._id)
        .filter((id): id is string => Boolean(id) && !taskIdsWithMetrics.has(id));
      if (missingTaskIds.length === 0) return;
      const toAdd: LocalMetric[] = missingTaskIds.map(taskId => ({
        name: '',
        type: TrackerMetricTypes.Boolean,
        typeOption: 'times',
        taskId,
        uid: this.generateUid(),
      }));
      this.localMetrics.update(m => [...m, ...toAdd]);
    },
    {allowSignalWrites: true}
  );

  // Computed signals - get tasks from objectives (which have metrics populated)
  selectedTasks = computed(() => {
    const objectives = this.cbProject()?.challengeBuilder?.objectives || [];
    const allTasks = objectives.flatMap(obj => obj.tasks || []);
    return allTasks.filter(task => task.selected);
  });

  // Group metrics by task name for display (each task has at least one metric via ensureEmptyMetricPerTaskEffect)
  tMetricGroups = computed(() => {
    const tasks = this.selectedTasks();
    const metrics = this.localMetrics();

    return tasks.map(task => ({
      taskName: task.name,
      taskId: task._id || '',
      metrics: metrics.filter(m => m.taskId === task._id),
    }));
  });

  // Computed: UIDs of metrics that have duplicate names within their task group
  duplicateMetricUids = computed(() => {
    const groups = this.tMetricGroups();
    const duplicates = new Set<string>();

    for (const group of groups) {
      const nameToUids = new Map<string, string[]>();

      for (const metric of group.metrics) {
        const name = (metric.name || '').trim().toLowerCase();
        if (!name) continue;

        const uids = nameToUids.get(name) || [];
        uids.push(metric.uid);
        nameToUids.set(name, uids);
      }

      for (const uids of nameToUids.values()) {
        if (uids.length > 1) {
          uids.forEach(uid => duplicates.add(uid));
        }
      }
    }

    return duplicates;
  });

  // Validation status
  isFormValid = computed(() => {
    const groups = this.tMetricGroups();
    const hasDuplicates = this.duplicateMetricUids().size > 0;
    return (
      !hasDuplicates && groups.every(group => group.metrics.length > 0 && group.metrics.every(m => m.name?.trim()))
    );
  });

  // Effect to update validation state
  // @ts-expect-error - Effect must be assigned to trigger registration
  private readonly validationEffect = effect(
    () => {
      const isValid = this.isFormValid();
      this.store$.dispatch(new UpdateChallengeNextValidAction({valid: isValid}));
    },
    {allowSignalWrites: true}
  );

  // Type options
  readonly metricTypeOptions: MetricTypeOption[] = [
    {value: TrackerMetricTypes.Boolean, display: 'yes/no'},
    {value: TrackerMetricTypes.Numeric, display: 'amount'},
    {value: TrackerMetricTypes.Duration, display: 'time/duration'},
    {value: TrackerMetricTypes.Percentage, display: 'percentage'},
  ];

  // Suffix options for numeric type
  readonly numericSuffixOptions: MetricSuffixOption[] = suffixOptions.map(option => ({
    value: option,
    display: option,
  }));

  // Duration type options
  readonly durationTypeOptions: MetricSuffixOption[] = [
    {value: 'hrs', display: 'hours'},
    {value: 'hrs/min', display: 'hrs : min'},
    {value: 'min', display: 'minutes'},
    {value: 'min/sec', display: 'min : sec'},
  ];

  private initTaskIcons(cbProject: ChallengeBuilderProject): void {
    const objectives = cbProject.challengeBuilder?.objectives || [];
    const tasks = objectives.flatMap(obj => obj.tasks || []).filter(t => t.selected);
    const icons: Record<string, string> = {};

    for (const task of tasks) {
      if (!task._id) continue;
      if (task.icon) {
        icons[task._id] = task.icon;
      } else if (task.iconSuggestion) {
        const resolved = this.tasksService.findIconByKeyword(task.iconSuggestion);
        if (resolved) {
          icons[task._id] = resolved;
        }
      }
    }

    this.taskIcons.set(icons);
  }

  getTaskIcon(taskId: string): string {
    return this.taskIcons()[taskId] || '';
  }

  onTaskIconChange(taskId: string, icon: string): void {
    this.taskIcons.update(icons => ({...icons, [taskId]: icon}));
  }

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
      this.nextClickSubscription?.unsubscribe();
      this.backClickSubscription?.unsubscribe();
    });
  }

  ngOnInit(): void {
    this.nextClickSubscription = this.nextClick$().subscribe(() => {
      this.onNextClick();
    });
    this.backClickSubscription = this.backClick$().subscribe(() => {
      this.onBackClick();
    });
  }

  getTypeDisplay(type: TrackerMetricTypes): string {
    return this.metricTypeOptions.find(opt => opt.value === type)?.display || '';
  }

  getTypeOptionDisplay(metric: ChallengeMetric): string {
    const type = metric.type;
    const typeOption = metric.typeOption;

    if (type === TrackerMetricTypes.Boolean || type === TrackerMetricTypes.Numeric) {
      return typeOption || '';
    } else if (type === TrackerMetricTypes.Duration) {
      const option = this.durationTypeOptions.find(opt => opt.value === typeOption);
      return option?.display || '';
    }

    return '';
  }

  getSuffixOptionsForType(type: TrackerMetricTypes): MetricSuffixOption[] {
    if (type === TrackerMetricTypes.Numeric) {
      return this.numericSuffixOptions;
    } else if (type === TrackerMetricTypes.Boolean) {
      return [{value: 'times', display: 'times'}];
    } else if (type === TrackerMetricTypes.Duration) {
      return this.durationTypeOptions;
    }
    return [];
  }

  shouldShowTypeOption(type: TrackerMetricTypes): boolean {
    return type !== TrackerMetricTypes.Percentage;
  }

  onTypeChange(metricIndex: number, type: TrackerMetricTypes): void {
    this.localMetrics.update(metrics => {
      const updated = [...metrics];
      updated[metricIndex] = {...updated[metricIndex], type};

      // Set default typeOption based on type
      if (type === TrackerMetricTypes.Boolean) {
        updated[metricIndex].typeOption = 'times';
      } else if (type === TrackerMetricTypes.Numeric) {
        updated[metricIndex].typeOption = 'units';
      } else if (type === TrackerMetricTypes.Duration) {
        updated[metricIndex].typeOption = 'hrs';
      } else if (type === TrackerMetricTypes.Percentage) {
        updated[metricIndex].typeOption = '';
      }

      return updated;
    });
  }

  onTypeOptionChange(metricIndex: number, typeOption: string): void {
    this.localMetrics.update(metrics => {
      const updated = [...metrics];
      updated[metricIndex] = {...updated[metricIndex], typeOption};
      return updated;
    });
  }

  onNameChange(metricIndex: number, name: string): void {
    this.localMetrics.update(metrics => {
      const updated = [...metrics];
      updated[metricIndex] = {...updated[metricIndex], name};
      return updated;
    });
  }

  addMetric(taskId: string): void {
    const newMetric: LocalMetric = {
      name: '',
      type: TrackerMetricTypes.Boolean,
      typeOption: 'times',
      taskId,
      uid: this.generateUid(),
    };

    this.localMetrics.update(metrics => [...metrics, newMetric]);

    // Focus the new input
    setTimeout(() => {
      const inputs = document.querySelectorAll(`[data-task="${taskId}"] input.metric-name-input`);
      const lastInput = inputs[inputs.length - 1] as HTMLInputElement;
      lastInput?.focus();
    }, 0);
  }

  removeMetric(metricIndex: number): void {
    const metrics = this.localMetrics();
    const metricToRemove = metrics[metricIndex];
    const taskMetrics = metrics.filter(m => m.taskId === metricToRemove.taskId);

    // Only allow removal if there's more than one metric for this task
    if (taskMetrics.length > 1) {
      this.localMetrics.update(m => m.filter((_, i) => i !== metricIndex));
    }
  }

  getMetricIndex(taskId: string, localIndex: number): number {
    const metrics = this.localMetrics();
    let count = 0;
    for (let i = 0; i < metrics.length; i++) {
      if (metrics[i].taskId === taskId) {
        if (count === localIndex) {
          return i;
        }
        count++;
      }
    }
    return -1;
  }

  /**
   * Determines if a metric has been altered compared to the initial state.
   * A metric is considered altered if:
   * - It is a new metric (no matching initial metric by _id), OR
   * - It has been modified (name, type, or typeOption changed)
   */
  private isMetricAltered(metric: LocalMetric): boolean {
    // Find the corresponding initial metric by _id if available, otherwise fall back to taskId + name
    const initial = metric._id
      ? this.initialMetrics.find(m => m._id === metric._id)
      : this.initialMetrics.find(m => m.taskId === metric.taskId && m.name === metric.name);

    // If no initial metric found, it's a new metric
    if (!initial) {
      return true;
    }

    // Check if any property changed (name is only checked when matched by _id)
    const nameChanged = initial.name !== metric.name;
    const isModified =
      nameChanged || initial.type !== metric.type || initial.typeOption !== metric.typeOption || !metric.target;

    return isModified;
  }

  private getAlteredMetrics(metrics: LocalMetric[]): LocalMetric[] {
    return metrics.filter(metric => this.isMetricAltered(metric));
  }

  private onNextClick(): void {
    if (!this.isFormValid()) {
      this.messageService.add('Please complete all metrics', 'error');
      this.store$.dispatch(new SetNextLoadingAction({nextLoading: false}));
      return;
    }

    const metricsAltered = this.getAlteredMetrics(this.localMetrics());
    this.saveMetrics(metricsAltered, () => this.onNext.emit());
  }

  private onBackClick(): void {
    this.saveMetrics([], () => this.onBack.emit());
  }

  private saveMetrics(metricsAltered: LocalMetric[], onSuccess?: () => void): void {
    this.store$.dispatch(
      new ChallengeMetricsAction({
        projectId: this.cbProject()?._id || '',
        metrics: this.localMetrics(),
        metricsAltered,
        onSuccess: () => {
          if (this.destroyed) return;
          onSuccess?.();
          this.store$.dispatch(new SetNextLoadingAction({nextLoading: false}));
          this.store$.dispatch(new SetBackLoadingAction({backLoading: false}));
        },
        onFailure: (error: Error) => {
          if (this.destroyed) return;
          this.messageService.add('Failed to save metrics', 'error');
          console.error('Failed to save metrics:', error);
          this.store$.dispatch(new SetNextLoadingAction({nextLoading: false}));
          this.store$.dispatch(new SetBackLoadingAction({backLoading: false}));
        },
      })
    );
  }
}
